import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloudOffRoundedIcon from '@mui/icons-material/CloudOffRounded';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import { memo } from 'react';
import {
  DETAIL_LAYOUT_SX,
  SURFACE_SX,
  formatValue,
} from './customerDetailUtils';

function BlockedSection({ actionLabel, children, title }) {
  return (
    <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 2, md: 2.25 } }}>
      <Stack spacing={1}>
        <Typography variant="h6" fontWeight={700}>
          {title}
        </Typography>
        {children}
        {actionLabel ? (
          <Button
            type="button"
            variant="outlined"
            disabled
            startIcon={<AddRoundedIcon fontSize="small" />}
            sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
          >
            {actionLabel}
          </Button>
        ) : null}
      </Stack>
    </Paper>
  );
}

const CustomerDetailBlockedState = memo(function CustomerDetailBlockedState({ customerNumber }) {
  return (
    <Stack spacing={2}>
      <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 2, md: 2.25 } }}>
        <Stack spacing={1.25}>
          <Alert severity="warning" icon={<CloudOffRoundedIcon fontSize="inherit" />}>
            Fortnox connection required to load CRM customer detail.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            The customer detail shell stays available, but all CRM reads and writes remain blocked until Fortnox can resolve customer data again.
          </Typography>
        </Stack>
      </Paper>

      <Box sx={DETAIL_LAYOUT_SX}>
        <Stack spacing={2}>
          <BlockedSection title="Open activities" actionLabel="Add activity">
            <Typography variant="body2" color="text.secondary">
              Planned CRM work cannot be loaded or updated while the customer is blocked.
            </Typography>
          </BlockedSection>

          <BlockedSection title="Contacts" actionLabel="Add contact">
            <Typography variant="body2" color="text.secondary">
              Contact records stay unavailable until the customer can be resolved again.
            </Typography>
          </BlockedSection>

          <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 2, md: 2.25 } }}>
            <Stack spacing={1}>
              <Typography variant="h6" fontWeight={700}>
                CRM overlay
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tenant-local CRM notes remain read-only while Fortnox is disconnected.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Customer number: {formatValue(customerNumber)}
              </Typography>
              <Button
                type="button"
                variant="contained"
                disabled
                sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
              >
                Save CRM profile
              </Button>
            </Stack>
          </Paper>
        </Stack>

        <Stack spacing={2}>
          <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 2, md: 2.25 } }}>
            <Stack spacing={0.75}>
              <Typography variant="h6" fontWeight={700}>
                Timeline
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Completed activities stay blocked while Fortnox is disconnected.
              </Typography>
            </Stack>
          </Paper>

          <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 2, md: 2.25 } }}>
            <Stack spacing={0.75}>
              <Typography variant="h6" fontWeight={700}>
                Fortnox customer data
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Read-only master customer details are unavailable until Fortnox is connected again.
              </Typography>
            </Stack>
          </Paper>
        </Stack>
      </Box>
    </Stack>
  );
});

export default CustomerDetailBlockedState;
